import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, ArrowLeft, Download, Clock } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import ShareButtons from "@/components/editorial/ShareButtons";
import DownloadGateModal from "@/components/DownloadGateModal";
import { fetchArticle } from "@/lib/articles";
import api from "@/lib/api";

const SITE_URL = "https://mujeres-motor.com";
const CATEGORY_LABELS = {
  "guía": "Guía", "opinión": "Opinión", "entrevista": "Entrevista",
  "toolkit": "Toolkit", "oportunidad": "Oportunidad",
};

export default function ResourceDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, notFound: false, data: null });
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setState({ loading: true, notFound: false, data: null });
    fetchArticle(slug)
      .then((data) => setState({ loading: false, notFound: false, data }))
      .catch((e) => setState({ loading: false, notFound: e.response?.status === 404, data: null }));
  }, [slug]);

  if (state.loading) return <SiteLayout><div className="min-h-screen flex items-center justify-center"><p className="text-charcoal/50">Cargando…</p></div></SiteLayout>;
  if (state.notFound) return <SiteLayout><div className="min-h-screen flex flex-col items-center justify-center gap-4"><p className="font-serif text-3xl">Artículo no encontrado</p><button onClick={() => nav("/resources")} className="underline">Ver recursos</button></div></SiteLayout>;

  const { article: a, related } = state.data;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    image: a.cover_image_url,
    datePublished: a.published_at,
    dateModified: a.updated_at,
    author: a.author_name ? { "@type": "Person", name: a.author_name } : undefined,
    publisher: { "@type": "Organization", name: "Mujeres Motor" },
    mainEntityOfPage: `${SITE_URL}/resources/${a.slug}`,
    description: a.excerpt,
  };

  return (
    <SiteLayout transparent>
      <></>

      {/* Hero */}
      <section data-testid="article-hero" className="relative min-h-[55vh] flex items-end bg-charcoal text-cream overflow-hidden pb-14">
        {a.cover_image_url && <img src={a.cover_image_url} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-55" />}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/55 to-charcoal" />
        <Container className="relative z-10">
          <Link to="/resources" className="inline-flex items-center gap-1.5 text-cream/60 text-xs uppercase tracking-[0.2em] hover:text-gold mb-6"><ArrowLeft className="h-3 w-3" />Recursos</Link>
          <p className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">{CATEGORY_LABELS[a.category] || a.category} · {a.reading_time_minutes} min</p>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">{a.title}</h1>
          {a.subtitle && <p className="mt-6 text-cream/75 text-lg md:text-xl font-serif italic max-w-2xl">{a.subtitle}</p>}
          <div className="mt-8 flex items-center gap-5 text-sm text-cream/70">
            {a.author_name && (
              <div className="flex items-center gap-3">
                {a.author_photo_url && <img src={a.author_photo_url} alt={a.author_name} className="w-10 h-10 rounded-full object-cover" />}
                <div>
                  <p className="text-cream">{a.author_name}</p>
                  {a.author_role && <p className="text-xs text-cream/55">{a.author_role}</p>}
                </div>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* Body */}
      <section className="bg-cream py-20 md:py-24">
        <Container>
          <article className="max-w-2xl mx-auto prose prose-slate prose-h2:font-serif prose-h2:font-light prose-h2:text-3xl md:prose-h2:text-4xl prose-h2:text-charcoal prose-h2:mt-12 prose-h3:font-serif prose-h3:font-light prose-h3:text-2xl prose-h3:text-charcoal prose-blockquote:border-l-4 prose-blockquote:border-terracotta prose-blockquote:text-charcoal prose-blockquote:italic prose-blockquote:not-italic prose-a:text-terracotta prose-a:no-underline hover:prose-a:underline prose-strong:text-charcoal prose-p:text-charcoal/85 prose-p:leading-[1.75] prose-li:text-charcoal/85 first-letter:font-serif first-letter:text-6xl first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:text-terracotta">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body}</ReactMarkdown>
          </article>
        </Container>
      </section>

      {/* Download */}
      {a.downloadable_asset_url && (
        <section className="bg-cream-dark py-14 border-y border-charcoal/5" data-testid="article-download">
          <Container>
            <div className="max-w-3xl mx-auto flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <Eyebrow tone="terracotta" className="mb-2 block">Descargable</Eyebrow>
                <h3 className="font-serif font-light text-2xl md:text-3xl text-charcoal leading-tight">Descarga el recurso completo.</h3>
                <p className="text-charcoal/70 text-sm mt-2">Versión PDF editorial con tablas y referencias extendidas.</p>
              </div>
              <button
                onClick={() => setGateOpen(true)}
                data-testid="article-download-btn"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm hover:bg-[#A64826]"
              >
                <Download className="h-4 w-4" /> Descargar PDF
              </button>
            </div>
          </Container>
          <DownloadGateModal
            open={gateOpen}
            onClose={() => setGateOpen(false)}
            downloadUrl={`${api.defaults.baseURL}/articles/${a.slug}/download`}
            title={`Descarga ${a.title}`}
            subtitle="PDF editorial. Si quieres, déjanos tu email para recibir nuevos recursos cada mes."
          />
        </section>
      )}

      {/* Author */}
      {a.author_name && (
        <section className="bg-cream py-14">
          <Container>
            <div className="max-w-2xl mx-auto flex items-center gap-5 p-6 border border-charcoal/10 rounded-sm bg-cream-dark">
              {a.author_photo_url && <img src={a.author_photo_url} alt={a.author_name} className="w-16 h-16 rounded-full object-cover" />}
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-1">Autor</p>
                <p className="font-serif text-xl text-charcoal">{a.author_name}</p>
                {a.author_role && <p className="text-sm text-charcoal/60">{a.author_role}</p>}
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* Share */}
      <section className="bg-cream-dark py-10 border-t border-charcoal/5">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <ShareButtons url={`${SITE_URL}/resources/${a.slug}`} title={a.title} type="resources" slug={a.slug} />
          </div>
        </Container>
      </section>

      {/* Related */}
      {related && related.length > 0 && (
        <section className="bg-cream py-20" data-testid="article-related">
          <Container>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow tone="terracotta" className="mb-4 block">Lecturas relacionadas</Eyebrow>
              <h3 className="font-serif font-light text-3xl md:text-4xl text-charcoal">Más recursos editoriales.</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {related.map((r) => (
                <Link key={r.id} to={`/resources/${r.slug}`} className="group">
                  <div className="aspect-[4/3] overflow-hidden rounded-sm mb-4 bg-charcoal-soft">
                    {r.cover_image_url && <img src={r.cover_image_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms]" />}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-1">{CATEGORY_LABELS[r.category] || r.category} · {r.reading_time_minutes} min</p>
                  <h3 className="font-serif text-xl text-charcoal group-hover:text-terracotta font-light">{r.title}</h3>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      )}
    </SiteLayout>
  );
}
